// src/pages/AdminCategoriesPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Alert,
  Snackbar,
  TextField
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  ViewList as ViewListIcon,
  DragIndicator as DragIcon,
  BarChart as ChartIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  PendingActions as PendingIcon,
  Archive as ArchiveIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useCategories } from '../context/CategoryContext';
import AdminCategoryListTab from '../components/categories/AdminCategoryListTab';
import CategoryForm from '../components/categories/CategoryForm';
import CategoryDragList from '../components/categories/CategoryDragList';
import CategoryStats from '../components/categories/CategoryStats';
import PendingCategoriesList from '../components/categories/PendingCategoriesList';
import InactiveCategoriesList from '../components/categories/InactiveCategoriesList';
import ConfirmDialog from '../components/common/ConfirmDialog';
import categoryService from '../services/categoryService';

const AdminCategoriesPage = () => {
  const { createCategory, updateCategory, deleteCategory } = useCategories();
  
  // Charger l'onglet mémorisé depuis localStorage ou utiliser 0 par défaut
  const [tabValue, setTabValue] = useState(() => {
    const savedTab = localStorage.getItem('adminCategoriesTab');
    return savedTab ? parseInt(savedTab, 10) : 0;
  });

  // Mémoriser l'onglet actif dans localStorage quand il change
  useEffect(() => {
    localStorage.setItem('adminCategoriesTab', tabValue.toString());
  }, [tabValue]);
  const [openForm, setOpenForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isModerationMode, setIsModerationMode] = useState(false);
  const [rejectDialog, setRejectDialog] = useState({ open: false, reason: '' });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [cacheAction, setCacheAction] = useState({ loading: false, type: null });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenForm = (category = null) => {
    setSelectedCategory(category);
    setIsModerationMode(false);
    setOpenForm(true);
  };

  const handleOpenModerationForm = (category) => {
    setSelectedCategory(category);
    setIsModerationMode(true);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedCategory(null);
    setIsModerationMode(false);
  };

  const handleSubmitForm = async (data, isFormData = false) => {
    try {
      if (selectedCategory) {
        // Mode édition
        await updateCategory(selectedCategory.id, data);

        if (isModerationMode && selectedCategory.status === 'pending') {
          await categoryService.approveCategory(selectedCategory.id);
          setNotification({
            open: true,
            message: 'Catégorie modifiée et approuvée avec succès',
            severity: 'success'
          });
          window.dispatchEvent(new Event('refreshCategories'));
          handleCloseForm();
          return;
        }

        setNotification({
          open: true,
          message: 'Catégorie mise à jour avec succès',
          severity: 'success'
        });
      } else {
        // Mode création
        await createCategory(data);
        setNotification({
          open: true,
          message: 'Catégorie créée avec succès',
          severity: 'success'
        });
      }
      handleCloseForm();
    } catch (error) {
      // Re-throw: CategoryForm gère l'affichage inline des erreurs
      throw error;
    }
  };

  const handleOpenRejectDialog = () => {
    setRejectDialog({ open: true, reason: '' });
  };

  const handleCloseRejectDialog = () => {
    setRejectDialog({ open: false, reason: '' });
  };

  const handleRejectFromModeration = async () => {
    if (!selectedCategory?.id) return;
    if (!rejectDialog.reason.trim()) {
      setNotification({
        open: true,
        message: 'Veuillez indiquer une raison de rejet',
        severity: 'warning'
      });
      return;
    }

    try {
      await categoryService.rejectCategory(selectedCategory.id, rejectDialog.reason.trim());
      setNotification({
        open: true,
        message: 'Catégorie rejetée avec succès',
        severity: 'success'
      });
      handleCloseRejectDialog();
      handleCloseForm();
      window.dispatchEvent(new Event('refreshCategories'));
    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.message || 'Erreur lors du rejet de la catégorie',
        severity: 'error'
      });
    }
  };

  const handleOpenDeleteConfirm = (category) => {
    setSelectedCategory(category);
    setConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteCategory(selectedCategory.id);
      setNotification({
        open: true,
        message: 'Catégorie supprimée avec succès',
        severity: 'success'
      });
    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.message || 'Une erreur est survenue lors de la suppression',
        severity: 'error'
      });
    } finally {
      setConfirmDelete(false);
      setSelectedCategory(null);
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const showNotification = ({ message, severity }) => {
    setNotification({ open: true, message, severity });
  };

  const handleCacheAction = async (type) => {
    try {
      setCacheAction({ loading: true, type });
      
      if (type === 'preload') {
        await categoryService.preloadCategoryCache();
        setNotification({
          open: true,
          message: 'Cache préchargé avec succès',
          severity: 'success'
        });
      } else if (type === 'clear') {
        await categoryService.clearCategoryCache();
        setNotification({
          open: true,
          message: 'Cache vidé avec succès',
          severity: 'success'
        });
      }
    } catch (error) {
      setNotification({
        open: true,
        message: `Erreur lors de l'action sur le cache: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setCacheAction({ loading: false, type: null });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ 
              fontWeight: 700,
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}>
              Gestion des Catégories
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Créez, modifiez et organisez vos catégories
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={() => handleCacheAction('preload')}
              disabled={cacheAction.loading}
              size="small"
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Précharger le cache
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<DeleteIcon />}
              onClick={() => handleCacheAction('clear')}
              disabled={cacheAction.loading}
              size="small"
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Vider le cache
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenForm()}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4
                }
              }}
            >
              Nouvelle catégorie
            </Button>
          </Box>
        </Box>
        
        <Paper sx={{ mb: 4, borderRadius: 3, overflow: 'hidden', boxShadow: 3 }}>
          <Box sx={{ 
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            borderBottom: 1, 
            borderColor: 'divider' 
          }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="category management tabs"
              centered
              sx={{ 
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 64
                }
              }}
            >
              <Tab icon={<ViewListIcon />} label="Liste" id="tab-0" />
              <Tab icon={<PendingIcon />} label="En attente d'approbation" id="tab-1" />
              <Tab icon={<ArchiveIcon />} label="Non actives" id="tab-2" />
              <Tab icon={<DragIcon />} label="Réorganiser" id="tab-3" />
              <Tab icon={<ChartIcon />} label="Statistiques" id="tab-4" />
            </Tabs>
          </Box>
          
          <Box sx={{ p: 3 }}>
            <Box role="tabpanel" hidden={tabValue !== 0} id="tabpanel-0" aria-labelledby="tab-0">
              {tabValue === 0 && (
                <AdminCategoryListTab onNotification={showNotification} />
              )}
            </Box>
            
            <Box role="tabpanel" hidden={tabValue !== 1} id="tabpanel-1" aria-labelledby="tab-1">
              {tabValue === 1 && (
                <PendingCategoriesList 
                  onPreviewCategory={handleOpenModerationForm}
                  onActionSuccess={() => {
                    // Rafraîchir les données après une action
                    window.dispatchEvent(new Event('refreshCategories'));
                  }}
                />
              )}
            </Box>
            
            <Box role="tabpanel" hidden={tabValue !== 2} id="tabpanel-2" aria-labelledby="tab-2">
              {tabValue === 2 && (
                <InactiveCategoriesList 
                  onActionSuccess={() => {
                    // Rafraîchir les données après une action
                    window.dispatchEvent(new Event('refreshCategories'));
                  }}
                />
              )}
            </Box>
            
            <Box role="tabpanel" hidden={tabValue !== 3} id="tabpanel-3" aria-labelledby="tab-3">
              {tabValue === 3 && <CategoryDragList />}
            </Box>
            
            <Box role="tabpanel" hidden={tabValue !== 4} id="tabpanel-4" aria-labelledby="tab-4">
              {tabValue === 4 && <CategoryStats />}
            </Box>
          </Box>
        </Paper>
        
        {/* Dialogue de formulaire */}
        <Dialog 
          open={openForm} 
          onClose={handleCloseForm}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {isModerationMode
              ? 'Modérer la catégorie soumise'
              : selectedCategory
                ? 'Modifier la catégorie'
                : 'Créer une nouvelle catégorie'}
            <IconButton
              aria-label="close"
              onClick={handleCloseForm}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <CategoryForm
              category={selectedCategory}
              onSubmit={handleSubmitForm}
              onCancel={handleCloseForm}
              submitButtonLabel={isModerationMode ? 'Approuver' : null}
              showRejectAction={isModerationMode}
              onReject={handleOpenRejectDialog}
              rejectButtonLabel="Rejeter"
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={rejectDialog.open}
          onClose={handleCloseRejectDialog}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Rejeter la catégorie</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Cette action rejettera la catégorie "{selectedCategory?.name}".
            </Alert>
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={rejectDialog.reason}
              onChange={(e) => setRejectDialog((prev) => ({ ...prev, reason: e.target.value }))}
              placeholder="Indiquez la raison du rejet..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRejectDialog}>Annuler</Button>
            <Button
              onClick={handleRejectFromModeration}
              variant="contained"
              color="error"
              disabled={!rejectDialog.reason.trim()}
            >
              Rejeter
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Dialogue de confirmation de suppression */}
        <ConfirmDialog
          open={confirmDelete}
          title="Confirmer la suppression"
          content={`Êtes-vous sûr de vouloir supprimer la catégorie "${selectedCategory?.name}" ? Cette action est irréversible.`}
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirmDelete(false)}
        />
        
        {/* Notification */}
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseNotification} severity={notification.severity}>
            {notification.message}
          </Alert>
        </Snackbar>
      </motion.div>
    </Container>
  );
};

export default AdminCategoriesPage;